import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';

import { ReviewCompliancePanelProps } from './ReviewCompliancePanel.props';
import { ServiceBillingInfo } from '../../../../../@types/service.type';
import { Panel } from '../../../../../components/atoms/Panel';

const labelSx = { fontSize: '12px', fontWeight: 600, color: 'text.secondary', mb: 0.25 };

const BILLABLE_TYPES = ['None', 'Annuity', 'Billable', 'Duplicate', 'Not Billable'] as const;
const BILLABLE_TYPE_MAP: Record<string, string> = {
  none: 'None', annuity: 'Annuity', billable: 'Billable',
  duplicate: 'Duplicate', 'not-billable': 'Not Billable', 'not billable': 'Not Billable',
};
const BILLABLE_CODE_MAP: Record<string, string> = {
  None: 'none', Annuity: 'annuity', Billable: 'billable',
  Duplicate: 'duplicate', 'Not Billable': 'not-billable',
};

const toDisplayType = (code?: string) => BILLABLE_TYPE_MAP[code ?? ''] ?? 'None';

const EMPTY_STATE: ServiceBillingInfo = {
  billingTypeCode: 'none',
  isReviewed: false,
  isClientInvoiced: false,
  billingNotes: '',
  privateNotes: '',
  invoices: [],
};

const YesNoToggle: React.FC<{
  value: boolean;
  onChange: (val: boolean) => void;
  ariaLabel: string;
}> = ({ value, onChange, ariaLabel }) => (
  <Box sx={{ display: 'flex', gap: 0 }}>
    <Box
      component="button"
      role="radio"
      aria-checked={value}
      aria-label={`${ariaLabel} Yes`}
      onClick={() => onChange(true)}
      sx={{
        px: 1.5, py: 0.25, fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
        border: '1px solid', borderRadius: '4px 0 0 4px', cursor: 'pointer',
        borderColor: value ? 'success.main' : 'v4.border',
        bgcolor: value ? 'success.main' : 'transparent',
        color: value ? 'common.white' : 'text.secondary',
      }}
    >
      Yes
    </Box>
    <Box
      component="button"
      role="radio"
      aria-checked={!value}
      aria-label={`${ariaLabel} No`}
      onClick={() => onChange(false)}
      sx={{
        px: 1.5, py: 0.25, fontSize: '12px', fontWeight: 600, fontFamily: 'inherit',
        border: '1px solid', borderRadius: '0 4px 4px 0', cursor: 'pointer',
        borderColor: !value ? 'error.main' : 'v4.border',
        bgcolor: !value ? 'error.main' : 'transparent',
        color: !value ? 'common.white' : 'text.secondary',
      }}
    >
      No
    </Box>
  </Box>
);

export const ReviewCompliancePanel: React.FC<ReviewCompliancePanelProps> = ({
  billingInfo,
  onSave,
}) => {
  const [form, setForm] = useState<ServiceBillingInfo>(EMPTY_STATE);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (billingInfo) {
      setForm({ ...billingInfo });
      setDirty(false);
    }
  }, [billingInfo]);

  const update = <K extends keyof ServiceBillingInfo>(key: K, value: ServiceBillingInfo[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    onSave?.(form);
    setDirty(false);
  };

  return (
    <Panel title="Review & Compliance">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Toggles */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: 1, borderColor: 'v4.border' }}>
            <Typography sx={{ fontSize: '13px' }}>Client Invoiced</Typography>
            <YesNoToggle
              value={form.isClientInvoiced}
              onChange={(val) => update('isClientInvoiced', val)}
              ariaLabel="Client Invoiced"
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderBottom: 1, borderColor: 'v4.border' }}>
            <Typography sx={{ fontSize: '13px' }}>Compliance Review</Typography>
            <YesNoToggle
              value={form.isReviewed}
              onChange={(val) => update('isReviewed', val)}
              ariaLabel="Compliance Review"
            />
          </Box>
        </Box>

        {/* Billable Type */}
        <Box>
          <Typography sx={labelSx}>Billable Type</Typography>
          <Select
            size="small"
            value={toDisplayType(form.billingTypeCode)}
            onChange={(e) => update('billingTypeCode', BILLABLE_CODE_MAP[e.target.value] ?? 'none')}
            sx={{ fontSize: '13px', minWidth: 180 }}
            inputProps={{ 'aria-label': 'Billable Type' }}
          >
            {BILLABLE_TYPES.map((t) => (
              <MenuItem key={t} value={t} sx={{ fontSize: '13px' }}>{t}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Billing Notes */}
        <Box>
          <Typography sx={labelSx}>Billing Notes</Typography>
          <TextField
            size="small" multiline minRows={2} fullWidth
            placeholder="Billing notes..."
            value={form.billingNotes}
            onChange={(e) => update('billingNotes', e.target.value)}
            inputProps={{ 'aria-label': 'Billing notes' }}
            sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }}
          />
        </Box>

        {/* Private Notes */}
        <Box>
          <Typography sx={labelSx}>Private Notes</Typography>
          <TextField
            size="small" multiline minRows={2} fullWidth
            placeholder="Internal notes (not visible to client)..."
            value={form.privateNotes}
            onChange={(e) => update('privateNotes', e.target.value)}
            inputProps={{ 'aria-label': 'Private notes' }}
            sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }}
          />
        </Box>

        {/* Save */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            size="small"
            disabled={!dirty}
            onClick={handleSave}
            sx={{
              fontSize: '12px', textTransform: 'none', fontWeight: 600,
              bgcolor: 'v4.accent', '&:hover': { opacity: 0.9 },
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Panel>
  );
};
